/**
 * OCRImportModal — upload image, extract text via OCR (placeholder).
 */
import { useState, useRef } from 'react';
import { useConvex } from 'convex/react';
import { uploadAsset } from '@/lib/uploads';
import { X, FileImage } from 'lucide-react';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function OCRImportModal({ open, onClose }: Props) {
  const convex = useConvex();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f && f.type.startsWith('image/')) {
      setFile(f);
      setResult(null);
    }
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      await uploadAsset(convex, {
        file,
        type: 'flyer_reference',
        name: file.name,
      });
      setResult('Image uploaded. OCR extraction coming soon.');
      setFile(null);
    } catch (err) {
      setResult('Upload failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-border bg-card shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Import via OCR</h2>
          <button onClick={onClose} className="rounded p-1.5 text-muted-foreground hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Upload an image to extract text and import as flyer reference.</p>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        <div onClick={() => inputRef.current?.click()} className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50">
          {file ? <p className="text-sm font-medium truncate">{file.name}</p> : (
            <>
              <FileImage className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Click to select image</p>
            </>
          )}
        </div>
        {result && <p className="text-sm text-muted-foreground mt-3 p-2 rounded bg-muted/50">{result}</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={handleImport} disabled={!file || uploading} className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {uploading ? 'Uploading…' : 'Import'}
          </button>
          <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
        </div>
      </div>
    </div>
  );
}
